import { Box, Button, IconButton, Modal, Tooltip, Typography } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { TrashIcon } from '../../components/icons/TrashIcon'
import { useEffect, useState } from 'react'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { useTranslation } from 'react-i18next'
import closeModal from '../../assets/table_icons/delete.svg'
import styles from './ModalFamiliar.module.css'
import { GenericTooltip } from 'components/generic-tooltip'
import { emptyUserDTO, fromModel, User, UserDTO } from 'modules/users/models/User'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from 'modules/users'
import { LoggedUserService } from 'modules/users/services/LoggedUserService'
import { getUserContainer } from 'container/user-module'
import { Query } from 'common/api/Query'
import { PhoneInputWithCountrySelect } from '../../components/phone-input'
import { CustomModal } from '../../components/modal/CustomModal'
import { PatientFamilyDTO, emptyPatientFamily } from 'modules/users/models/PatientFamily'

interface FamiliarWithUser {
  family: PatientFamilyDTO
  user: User
}

interface ModalFamiliarProps {
  open: boolean
  onClose: () => void
  patientID: string
  // Para el titulo "Familiares de X" del diseno. Opcional: sin el se usa el
  // titulo generico de antes.
  patientName?: string
}

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function ModalFamiliar({ open, onClose, patientID, patientName }: ModalFamiliarProps) {
  const { t } = useTranslation()
  const [familiares, setFamiliares] = useState<FamiliarWithUser[]>([])
  const [showForm, setShowForm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [existingUser, setExistingUser] = useState<User | null>(null)
  const [isAlreadyFamiliar, setIsAlreadyFamiliar] = useState(false)
  const [isNonFamiliarUser, setIsNonFamiliarUser] = useState(false)
  // Familiar que se esta editando desde el lapiz. null = alta nueva.
  const [editingFamiliarID, setEditingFamiliarID] = useState<string | null>(null)
  // Familiar pendiente de confirmar borrado. Antes la papelera borraba directa.
  const [familiarToRemove, setFamiliarToRemove] = useState<FamiliarWithUser | null>(null)
  const loggedUser = loggedUserService.get()

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationship: '',
  })

  const getFamiliars = () => {
    setIsLoading(true)
    userService.getFamilies(patientID).subscribe((families) => {
      if (families.length === 0) {
        setFamiliares([])
        setIsLoading(false)
        return
      }

      const familiarIDs = families.map((f) => f.familiarID)
      userService
        .getFilteredList(
          new Query({
            query: [{ name: 'ids', value: familiarIDs }],
            pager: { offset: 0, limit: familiarIDs.length },
          })
        )
        .subscribe((res) => {
          const familiaresWithUsers = families
            .map((family) => {
              const user = res.items.find((u) => u.id === family.familiarID)
              return user ? { family, user } : null
            })
            .filter(Boolean) as FamiliarWithUser[]

          setFamiliares(familiaresWithUsers)
          setIsLoading(false)
        })
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'email') {
      setFormData({ ...formData, email: value })
      setExistingUser(null)
      setIsAlreadyFamiliar(false)
      setIsNonFamiliarUser(false)
    } else {
      setFormData({ ...formData, [name]: value })
    }

    if (name === 'email' && value.includes('@')) {
      userService
        .getFilteredList(
          new Query({
            query: [{ name: 'email', value: value }],
            pager: { offset: 0, limit: 1 },
          })
        )
        .subscribe((res) => {
          if (res.items.length > 0) {
            const foundUser = res.items[0]

            const isFamiliar = foundUser.roles.some((role) => role.toLowerCase() === 'familiar')

            if (!isFamiliar) {
              setExistingUser(null)
              setIsAlreadyFamiliar(false)
              setIsNonFamiliarUser(true)
              return
            }

            setExistingUser(foundUser)
            setIsNonFamiliarUser(false)

            const isAlreadyRelated = familiares.some((f) => f.user.id === foundUser.id)
            setIsAlreadyFamiliar(isAlreadyRelated)

            if (!isAlreadyRelated) {
              userService.getPatientsByFamiliar(foundUser.id).subscribe((families) => {
                const phoneFromRelation = families.length > 0 ? families[0].phone : ''

                setFormData((prev) => ({
                  ...prev,
                  firstName: foundUser.firstName,
                  lastName: foundUser.lastName,
                  email: foundUser.email,
                  phone: phoneFromRelation,
                }))
              })
            }
          } else {
            setExistingUser(null)
            setIsAlreadyFamiliar(false)
            setIsNonFamiliarUser(false)
          }
        })
    }
  }

  const handlePhoneChange = (phone: string) => {
    setFormData({ ...formData, phone })
  }

  // Se quito el boton de añadir de este modal: el alta se hace desde otro
  // sitio. La logica de alta (handleSave -> addFamiliar / addFamilyRelation) se
  // conserva por si vuelve a hacer falta, pero hoy no hay forma de llegar a
  // ella desde aqui; el formulario solo se abre para editar.

  const handleClose = () => {
    setShowForm(false)
    onClose()
  }

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      return false
    }
    if (isAlreadyFamiliar || isNonFamiliarUser) {
      return false
    }
    return true
  }

  const handleSave = () => {
    if (!validateForm()) {
      return
    }

    if (editingFamiliarID) {
      handleUpdateFamiliar()
      return
    }

    if (existingUser) {
      handleAddFamilyRelation(existingUser.id)
    } else {
      const newFamiliar: UserDTO = {
        ...emptyUserDTO(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        language: loggedUser?.language ?? 1,
        isPatient: false,
        isActive: true,
        patologies: [],
      }

      userService.addFamiliar(newFamiliar, navigator.language).subscribe((res) => {
        if (res) {
          handleAddFamilyRelation(res.id)
        }
      })
    }
  }

  const handleAddFamilyRelation = (familiarID: string) => {
    const familyRelation: PatientFamilyDTO = {
      ...emptyPatientFamily(patientID),
      familiarID: familiarID,
      relationship: formData.relationship,
      phone: formData.phone,
    }

    userService.addFamilyRelation(familyRelation).subscribe(() => {
      getFamiliars()
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        relationship: '',
      })
      setShowForm(false)
    })
  }

  const handleRemoveFamiliar = (familiarID: string) => {
    userService.removeFamilyRelation(patientID, familiarID).subscribe(() => {
      setFamiliarToRemove(null)
      getFamiliars()
    })
  }

  // Lapiz de la fila: abre el formulario con los datos del familiar. Solo se
  // pueden cambiar parentesco y telefono, que es lo que guarda la relacion;
  // el nombre y el email pertenecen al usuario familiar.
  const startEdit = (family: PatientFamilyDTO, user: User) => {
    setEditingFamiliarID(user.id)
    setExistingUser(user)
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: family.phone || '',
      relationship: family.relationship || '',
    })
    setShowForm(true)
  }

  const handleUpdateFamiliar = () => {
    if (!editingFamiliarID) return

    // El nombre y los apellidos son del usuario familiar; el parentesco y el
    // telefono, de la relacion. Se guardan por separado porque son dos cosas
    // distintas, aunque en el formulario aparezcan juntas.
    if (
      existingUser &&
      (formData.firstName !== existingUser.firstName || formData.lastName !== existingUser.lastName)
    ) {
      const dto = fromModel(existingUser)
      dto.firstName = formData.firstName
      dto.lastName = formData.lastName
      userService.update(dto).subscribe()
    }

    userService
      .updateFamilyRelation(patientID, editingFamiliarID, {
        ...emptyPatientFamily(patientID),
        familiarID: editingFamiliarID,
        relationship: formData.relationship,
        phone: formData.phone,
      })
      .subscribe(() => {
        setEditingFamiliarID(null)
        setShowForm(false)
        getFamiliars()
      })
  }

  useEffect(() => {
    if (open) {
      getFamiliars()
    }
  }, [open, patientID])

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={styles.modalContainer}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" className={styles.title}>
            {patientName ? t('relativesOf', { name: patientName }) : t('modalFamiliarsTitle')}
          </Typography>
          <GenericTooltip
            values={[t('close')]}
            icon={
              <img
                src={closeModal}
                onClick={handleClose}
                className={styles.icon}
                alt={t('close')}
              />
            }
            noUseFab={true}
          />
        </Box>

        <Box>
          {isLoading && <p>{t('loadingFamiliars')}</p>}
          {!isLoading && (
            <>
              {/* Tres columnas, como el diseno: nombre, parentesco y telefono.
                  Antes iba todo en una linea, con el parentesco entre
                  parentesis detras del nombre. */}
              {familiares.map(({ family, user }) => (
                <Box
                  key={family.id}
                  display="grid"
                  gridTemplateColumns="1.4fr 1fr 1.3fr auto"
                  alignItems="center"
                  gap={1}
                  sx={{ py: 1.25, borderBottom: '1px solid var(--gray-soft)' }}
                >
                  <Typography sx={{ fontSize: '14px' }}>
                    {user.firstName} {user.lastName}
                  </Typography>

                  <Typography sx={{ fontSize: '14px', color: 'var(--gray-text)' }}>
                    {family.relationship || '—'}
                  </Typography>

                  <Typography sx={{ fontSize: '14px', color: 'var(--gray-text)' }}>
                    {family.phone}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title={t('edit')}>
                      <IconButton
                        size="small"
                        onClick={() => startEdit(family, user)}
                        sx={{ p: 0.5, color: '#9b9d9a' }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('remove')}>
                      <IconButton
                        size="small"
                        onClick={() => setFamiliarToRemove({ family, user })}
                        sx={{ p: 0.5, color: 'var(--destructive, #e14c54)' }}
                      >
                        <TrashIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>

        {/* Sin boton de añadir: diseno confirma que el alta se hace desde otro
            sitio. El formulario de abajo sigue usandose, pero solo para editar
            una relacion existente desde el lapiz de la fila. */}
        {showForm && (
          <Box sx={{ mt: 2 }}>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
            >
              <Box>
                <Box mb={2}>
                  <Typography mb={1}>{t('email')}</Typography>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    required
                  />
                  {isAlreadyFamiliar && (
                    <Typography color="error" variant="caption" display="block" mt={1}>
                      {t('userAlreadyFamiliar')}
                    </Typography>
                  )}
                  {isNonFamiliarUser && (
                    <Typography color="error" variant="caption" display="block" mt={1}>
                      {t('emailBelongsToNonFamiliar')}
                    </Typography>
                  )}
                  {existingUser && !isAlreadyFamiliar && !isNonFamiliarUser && (
                    <Typography color="info.main" variant="caption" display="block" mt={1}>
                      {t('userFound')}
                    </Typography>
                  )}
                </Box>

                <Box mb={2} display="flex" justifyContent="space-between" gap={2}>
                  <Box width="50%">
                    <Typography mb={1}>{t('name')}</Typography>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      required
                      disabled={existingUser !== null && !editingFamiliarID}
                    />
                  </Box>

                  <Box width="50%">
                    <Typography mb={1}>{t('lastName')}</Typography>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      required
                      disabled={existingUser !== null && !editingFamiliarID}
                    />
                  </Box>
                </Box>

                <Box mb={2}>
                  <Typography mb={1}>{t('relationship')}</Typography>
                  <Input
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                  />
                </Box>

                <Box mb={2}>
                  <PhoneInputWithCountrySelect
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    label={t('phone')}
                    required
                    disabled={existingUser !== null && !editingFamiliarID}
                  />
                </Box>
              </Box>

              <Box display="flex" justifyContent="center" gap={2} mt={3}>
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={styles.buttonCancel}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className={styles.buttonAdd}
                  disabled={isAlreadyFamiliar || isNonFamiliarUser}
                >
                  {t('save')}
                </Button>
              </Box>
            </form>
          </Box>
        )}

        {/* Confirmacion antes de quitar un familiar: la papelera borraba de
            golpe, sin preguntar. */}
        <Modal open={Boolean(familiarToRemove)} onClose={() => setFamiliarToRemove(null)}>
          <CustomModal
            title={t('removeFamiliarTitle')}
            warningText={t('removeFamiliarWarning', {
              name: familiarToRemove
                ? `${familiarToRemove.user.firstName} ${familiarToRemove.user.lastName}`
                : '',
            })}
            destructive
            handleClose={() => setFamiliarToRemove(null)}
            handleSave={() => familiarToRemove && handleRemoveFamiliar(familiarToRemove.user.id)}
          />
        </Modal>
      </Box>
    </Modal>
  )
}
